import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import PostCard from "@/components/PostCard";
import { Stagger, StaggerItem, calmEase } from "@/components/motion/Reveal";
import { applySeo } from "@/lib/seo";

const HEADER_INITIAL = { opacity: 0, y: 32 };
const HEADER_VISIBLE = { opacity: 1, y: 0 };
const HEADER_TRANSITION = { duration: 0.7, ease: calmEase };

const titles = {
    news: {
        title: "NEWS", tag: "// THE FEED",
        desc: "What's breaking in tech, told straight.",
        seoTitle: "Tech News — Tizona Tech",
        seoDesc: "The latest tech news, gadget launches, and industry moves. Updated daily by Tizona Tech.",
    },
    reviews: {
        title: "REVIEWS", tag: "// THE VERDICT",
        desc: "Hands-on testing. Honest scores. No fluff.",
        seoTitle: "Tech Reviews — Tizona Tech",
        seoDesc: "Honest, hands-on reviews of phones, laptops, audio, and the gear that matters.",
    },
    videos: {
        title: "VIDEOS", tag: "// IN MOTION",
        desc: "Builds, breakdowns, and visual deep dives.",
        seoTitle: "Tech Videos — Tizona Tech",
        seoDesc: "Builds, breakdowns, and visual tech deep dives from Tizona Tech.",
    },
    features: {
        title: "FEATURES", tag: "// LONG READS",
        desc: "Deep dives, essays, interviews. The stories worth your full attention.",
        seoTitle: "Tech Features & Long Reads — Tizona Tech",
        seoDesc: "In-depth tech features, long-form essays, and interviews from Tizona Tech.",
    },
};

export default function Category() {
    const { cat } = useParams();
    const [posts, setPosts] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const meta = titles[cat] || {
        title: cat?.toUpperCase(),
        tag: "//",
        desc: "",
        seoTitle: `${cat?.toUpperCase()} — Tizona Tech`,
        seoDesc: "Tizona Tech",
    };

    useEffect(() => {
        applySeo({
            title: meta.seoTitle,
            description: meta.seoDesc,
            path: `/category/${cat}`,
            type: "website",
        });
    }, [cat, meta.seoTitle, meta.seoDesc]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/posts", { params: { category: cat, search: search || undefined } });
                setPosts(data);
            } catch {
                setPosts([]);
            } finally { setLoading(false); }
        })();
    }, [cat, search]);

    const reduceMotion = useReducedMotion();

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24" data-testid={`category-${cat}`}>
            <motion.div
                className="border-b border-zinc-800 pb-10 mb-12 grid md:grid-cols-12 gap-8 items-end"
                initial={reduceMotion ? false : HEADER_INITIAL}
                animate={HEADER_VISIBLE}
                transition={HEADER_TRANSITION}
            >
                <div className="md:col-span-8">
                    <div className="overline mb-4">{meta.tag}</div>
                    <h1 className="font-display text-white text-[14vw] md:text-[10rem] leading-[0.85] tracking-tight">
                        {meta.title}<span className="text-[#FFD000]">.</span>
                    </h1>
                    <p className="mt-6 text-zinc-400 max-w-2xl">{meta.desc}</p>
                </div>
                <div className="md:col-span-4">
                    <div className="relative">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search articles..."
                            className="tz-input"
                            data-testid="category-search-input"
                        />
                    </div>
                </div>
            </motion.div>

            {renderResults(loading, posts)}
        </div>
    );
}

function renderResults(loading, posts) {
    if (loading) {
        return (
            <div className="text-center py-16 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Loading...</div>
        );
    }
    if (posts.length === 0) {
        return (
            <div className="text-center py-24 border border-zinc-800" data-testid="category-empty">
                <div className="font-display text-3xl text-white mb-2">Nothing here yet.</div>
                <div className="text-zinc-500 text-sm">Check back soon — new posts drop weekly.</div>
            </div>
        );
    }
    return (
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800" stagger={0.08}>
            {posts.map(p => (
                <StaggerItem key={p.id} className="bg-[#0A0A0A]"><PostCard post={p} /></StaggerItem>
            ))}
        </Stagger>
    );
}
